/* Configurator engine constants — ported VERBATIM from frontend/public/lhc-app.html.
 *
 * Migration note (Step 1): these are the exact numeric constants the original engine
 * uses. Source line numbers from lhc-app.html are cited so parity is auditable. The
 * internal engine works in METRES; millimetres appear only at the backend export
 * boundary (buildBackendDesign, M = 1000).
 *
 * DO NOT change these values — they define behavior parity with the HTML configurator.
 */

// Walls (lhc-app.html ~32940–32942)
// [LOCK-W-08] WALL_THICKNESS is a GLOBAL the engine mutates: the Wall Properties
// panel's Thickness field (wp-thick) writes it for ALL walls at once. It is a
// mutable `let` binding + setter so every importer sees the live value (ES live
// bindings) — do NOT convert back to const, and do NOT capture it at module scope.
export let WALL_THICKNESS = 0.15;          // 15 cm (default)
export function setWallThickness(m) {
  if (typeof m === "number" && isFinite(m) && m > 0) WALL_THICKNESS = m;
}
export const DEFAULT_WALL_HEIGHT = 2.60;   // 2.6 m
export const M = 1000;                     // metres → millimetres (export only)

// Rule table — metres (lhc-app.html ~32951–32964, const LHC_RULES)
export const LHC_RULES = {
  PLINTH_H: 0.10,            // default plinth (toe-kick) height — project-overridable
  CARCASS_BASE_H: 0.72,     // base carcass body height (excl. plinth + worktop)
  WORKTOP_T: 0.038,         // worktop slab thickness
  SPLASH_GAP: 0.56,         // gap from worktop top up to wall-unit underside
  HOB_CLEAR_ELECTRIC: 0.50, // min vertical clearance above an electric hob
  HOB_CLEAR_GAS: 0.65,      // min vertical clearance above a gas hob
  CORRIDOR_MIN: 0.90,       // island/run walkway minimum (R2)
  CORRIDOR_REC: 1.05,       // island/run walkway recommended (R2)
  FILLER_MIN: 0.018,        // wall-end filler band minimum (F7)
  FILLER_MAX: 0.132,        // wall-end filler band maximum (F7)
  TILT_MARGIN: 0.01,        // tilt-up ceiling safety margin for tall units
  DOOR_WIDTH_SPLIT: 0.60,   // single-door leaf max before split advisory (m)
};

// Collision epsilons (lhc-app.html ~31677, 31806, 31887)
export const RULE_EPS = 0.002;   // 2 mm AABB grace (placement-vs-placement)
export const WALL_EPS = 0.005;   // 5 mm grace (cabinet-vs-wall SAT)

// Wall-unit mount line — hard-coded in the engine (getUpperLineM, ~32988: return 1.5)
export const UPPER_LINE_M = 1.5;

// Pricing (lhc-app.html ~31684, 38278)
export const LHC_SIZING_SURCHARGE = 0.20;  // 20% custom-size surcharge, once per cabinet
export const PG_MULT = {
  G1: 1.00, G2: 1.18, G3: 1.38, G4: 1.65,
  G5: 1.85, G6: 2.10, G7: 2.35, G8: 2.65, G9: 3.00,
};
// Default per-linear-metre rates (buildQuoteLines ~38356–38362)
export const DEFAULT_WORKTOP_RATE = 180;   // €/m
export const DEFAULT_PLINTH_RATE = 35;     // €/m
export const DEFAULT_CORNICE_RATE = 45;    // €/m
// DEFAULT_TAX_RATE removed 2026-07-19 — VAT does not exist on this platform.

// ── Phase-8 PRECISION: 0.1mm storage quantum ────────────────────────────────
// Every COMMITTED wall coordinate is quantized to 0.1mm (1e-4 m) at the storage
// boundary (makeWall + vertex/endpoint/props edits). Kills accumulated floating-
// point drift (live data had endpoints like 1.488443208724843) so joints that
// should coincide DO coincide, byte-exactly, on every subsequent edit. Pure math
// (mitres, previews, faces) stays full-float — only what is STORED is quantized.
export const QUANT_M = 1e-4;
// Multiply-form: Math.round(v*10000)/10000 yields THE closest double to the 4dp
// decimal (divide-form left 1e-16 residue and broke byte-equality with literals).
export const quant01 = (v) => Math.round(v * 10000) / 10000;
export const quantPt = (p) => [quant01(p[0]), quant01(p[1])];

// Snap thresholds (lhc-app.html ~32143, 32376; callers pass 0.7 drop / 0.8 drag)
export const SNAP_WALL_DIST = 0.6;         // m — default nearest-wall snap radius
export const SNAP_SIDE_MM = 30;            // mm — neighbor/wall-end magnet
export const SNAP_SIDE_LEADING_M = 0.050;  // m — directional leading-side reach (B4)

// Layer band bitmask (lhc-app.html ~32186 _layerCollides)
export const LAYER_BANDS = {
  'cabinet-tall': 15,   // low|mid|top|bridge
  'cabinet-base': 1,    // low
  'cabinet-wall': 2,    // mid
  'cabinet-stack': 4,   // top
  'cabinet-bridge': 8,  // bridge
};
